import type { QueryClient } from "@tanstack/react-query";
import { $api, fetchClient } from "../generated/client";

export type StringFilterCondition = "in" | "notIn";
export type StringFilterValue = string[];
export type DateFilterCondition = "between";
export type DateFilterValue = string[];

export type UseReturnRequestsQueryOptions = {
	shopId: string;
	search?: string | null;
	sortBy?:
		| "createdAt"
		| "orderName"
		| "shippedAt"
		| "shippingAt"
		| "treatedLogisticianAt"
		| "treatedMerchantAt"
		| null
		| undefined;
	sortOrder: "asc" | "desc";
	filter?: {
		status?: {
			condition: StringFilterCondition;
			value: StringFilterValue;
		};
		reason?: {
			condition: StringFilterCondition;
			value: StringFilterValue;
		};
		assignee?: {
			condition: StringFilterCondition;
			value: StringFilterValue;
		};
		createdDate?: {
			condition: DateFilterCondition;
			value: DateFilterValue;
		};
		treatedMerchantDate?: {
			condition: DateFilterCondition;
			value: DateFilterValue;
		};
		treatedLogisticianDate?: {
			condition: DateFilterCondition;
			value: DateFilterValue;
		};
		resolution?: {
			condition: StringFilterCondition;
			value: StringFilterValue;
		};
		returnStore?: {
			condition: StringFilterCondition;
			value: StringFilterValue;
		};
		warehouse?: {
			condition: StringFilterCondition;
			value: StringFilterValue;
		};
	};
	quickFilter?: "toBeTreated" | "toBeValidated";
	page?: number;
};

export default function useReturnRequests({
	shopId,
	search,
	sortBy,
	sortOrder,
	filter,
	quickFilter,
	page = 1,
}: UseReturnRequestsQueryOptions) {
	return $api.useQuery(
		"get",
		"/shops/{shopId}/return-requests/cursor",
		{
			params: {
				path: { shopId },
				query: {
					shopId,
					search: search ?? undefined,
					sortBy,
					sortOrder,
					quickFilter,
					page,
					statusCondition: filter?.status?.condition,
					statusValue: filter?.status?.value,
					reasonCondition: filter?.reason?.condition,
					reasonValue: filter?.reason?.value,
					assigneeCondition: filter?.assignee?.condition,
					assigneeValue: filter?.assignee?.value,
					createdDateCondition: filter?.createdDate?.condition,
					createdDateValue: filter?.createdDate?.value,
					treatedMerchantDateCondition: filter?.treatedMerchantDate?.condition,
					treatedMerchantDateValue: filter?.treatedMerchantDate?.value,
					treatedLogisticianDateCondition:
						filter?.treatedLogisticianDate?.condition,
					treatedLogisticianDateValue: filter?.treatedLogisticianDate?.value,
					resolutionCondition: filter?.resolution?.condition,
					resolutionValue: filter?.resolution?.value,
					returnStoreCondition: filter?.returnStore?.condition,
					returnStoreValue: filter?.returnStore?.value,
					locationCondition: filter?.warehouse?.condition,
					locationValue: filter?.warehouse?.value,
				},
			},
		},
		{
			refetchOnMount: "always",
			refetchOnWindowFocus: "always",
		},
	);
}

export const ensureReturnRequestPage = (
	queryClient: QueryClient,
	{
		shopId,
		search,
		sortBy,
		sortOrder,
		filter,
		quickFilter,
		page = 1,
	}: UseReturnRequestsQueryOptions,
) => {
	const params = {
		query: {
			search: search ?? undefined,
			sortBy,
			sortOrder,
			quickFilter,
			page,
			statusCondition: filter?.status?.condition,
			statusValue: filter?.status?.value,
			reasonCondition: filter?.reason?.condition,
			reasonValue: filter?.reason?.value,
			assigneeCondition: filter?.assignee?.condition,
			assigneeValue: filter?.assignee?.value,
			createdDateCondition: filter?.createdDate?.condition,
			createdDateValue: filter?.createdDate?.value,
			treatedMerchantDateCondition: filter?.treatedMerchantDate?.condition,
			treatedMerchantDateValue: filter?.treatedMerchantDate?.value,
			treatedLogisticianDateCondition:
				filter?.treatedLogisticianDate?.condition,
			treatedLogisticianDateValue: filter?.treatedLogisticianDate?.value,
			resolutionCondition: filter?.resolution?.condition,
			resolutionValue: filter?.resolution?.value,
			returnStoreCondition: filter?.returnStore?.condition,
			returnStoreValue: filter?.returnStore?.value,
			locationCondition: filter?.warehouse?.condition,
			locationValue: filter?.warehouse?.value,
		},
	};

	return queryClient.ensureQueryData({
		queryKey: ["get", "/return-requests", { params }],
		queryFn: async () => {
			const response = await fetchClient.GET(
				"/shops/{shopId}/return-requests/cursor",
				{ params: { path: { shopId }, query: params.query } },
			);
			return response.data;
		},
	});
};
